import { NavLink } from 'react-router-dom'
import { cn } from '@/utils'
import { ChevronLeft, ChevronRight, MessageCircleQuestion, X } from 'lucide-react'
import { useNavModel } from './useNavModel'
import type { NavRow } from './navModel'
import { UserMenu } from './UserMenu'
import type { ReactElement } from 'react'
import { Tip } from '@/components/ui'
import { ESCALATED_TIP, escalatedWords } from './sidebarText'
import { useHelpFeedback } from '@/components/helpFeedback/helpFeedbackContext'
import { ENTRY_LABEL, ENTRY_TIP } from '@/components/helpFeedback/helpFeedbackText'
import { HELP_SHORTCUT } from '@/components/helpFeedback/shortcut'

import { getBranding } from '@/lib/branding'
import { useBrandLogo } from '@/hooks/useBrandLogo'
import { SURFACES } from '@/custom'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  mobileOpen: boolean
  onMobileClose: () => void
}

/**
 * One row, the same look in every group (SA-962, SA-963). The pills render
 * only on the rows the model gives counts. Its height is navBudget.ts's
 * ROW_PX (SA-964): change the two together.
 */
/** Expanded, the badge carries its own tip; collapsed, the row's tip says the words. */
const withEscalatedTip = (collapsed: boolean, badge: ReactElement) => (collapsed ? badge : <Tip text={ESCALATED_TIP}>{badge}</Tip>)

function RowLink({ row, collapsed, onClick }: { row: NavRow; collapsed: boolean; onClick: () => void }) {
  const badge = row.badge ?? 0
  const escalated = row.escalated ?? 0
  // SA-1418 (B4): the escalation words ride the link's description and one tooltip, never a title.
  const escalation = escalated > 0 ? escalatedWords(escalated) : null
  const described = [row.tag, escalation].filter(Boolean).join(' · ') || undefined
  const link = (
    <NavLink
      to={row.to}
      onClick={onClick}
      end={row.end}
      data-tour-nav={row.to}
      aria-label={collapsed ? row.label : undefined}
      aria-description={described}
      className={({ isActive }) =>
        cn(
          'relative flex h-8 items-center gap-3 px-3 rounded-md text-sm transition-colors',
          'hover:bg-sidebar-accent',
          isActive
            ? 'bg-sidebar-accent text-sidebar-primary border-l-2 border-sidebar-primary'
            : 'text-sidebar-foreground/70',
          collapsed && 'lg:justify-center lg:px-2'
        )
      }
    >
      <row.icon className="size-4.5 shrink-0" />
      {!collapsed && <span className="truncate">{row.label}</span>}
      {/* SA-1097: the surface's tag ("Experimental"). aria-hidden keeps the link's name the label alone; the link's aria-description says the tag (SA-1107). */}
      {!collapsed && row.tag && (
        <span
          data-testid={`nav-tag-${row.to.replace('/', '') || 'home'}`}
          aria-hidden="true"
          className={cn(
            'rounded border border-sidebar-foreground/30 px-1 text-[9.5px] font-medium uppercase tracking-wider text-sidebar-foreground/60',
            badge > 0 || escalated > 0 ? 'ml-1' : 'ml-auto',
          )}
        >
          {row.tag}
        </span>
      )}
      {/* Escalated-to-human indicator (red, distinct from the unread pill). */}
      {escalated > 0 && withEscalatedTip(collapsed, (
        <span
          data-testid={`nav-escalated-${row.to.replace('/', '') || 'home'}`}
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-medium min-w-5 h-5 px-1.5',
            badge > 0 ? 'ml-1' : 'ml-auto',
            collapsed && 'lg:absolute lg:top-1 lg:left-1 lg:ml-0',
          )}
        >
          {escalated > 99 ? '99+' : escalated}
        </span>
      ))}
      {badge > 0 && (
        <span
          data-testid={`nav-badge-${row.to.replace('/', '') || 'home'}`}
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium min-w-5 h-5 px-1.5',
            escalated > 0 ? 'ml-1' : 'ml-auto',
            collapsed && 'lg:absolute lg:top-1 lg:right-1 lg:ml-0',
          )}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  )
  // SA-1034 PR 5 (spec §3.1): a collapsed rail shows the label as a tip, never a title.
  // The trigger is a span AROUND the NavLink, never the NavLink: Radix Slot
  // joins class names as strings, and NavLink's `className` is a function —
  // as the trigger it rendered its own source text as the class (review fix).
  // A focus on the link bubbles to the span, so the tip still opens by keyboard.
  return collapsed
    ? <Tip text={[row.label, row.tag, escalation].filter(Boolean).join(' · ')} side="right"><span className="block">{link}</span></Tip>
    : link
}

export function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const branding = getBranding()
  const logoUrl = useBrandLogo()
  const iconUrl = branding.iconUrl ?? '/favicon.webp'
  const helpFeedback = useHelpFeedback()
  const brandAlt = branding.appName ?? 'Secret Agents AI'

  // Every row and every gate is the model's (SA-960): this component decides
  // no visibility of its own.
  const nav = useNavModel(SURFACES)

  return (
    <aside
      className={cn(
        'print:hidden',
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar border-r border-sidebar-border',
        'transition-all duration-300 ease-in-out',
        // Desktop
        'lg:relative lg:translate-x-0',
        collapsed ? 'lg:w-16' : 'lg:w-64',
        // Mobile
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        'w-64'
      )}
    >
      {/* Logo section. Pinned (SA-958): without shrink-0 the overflowing
          column squeezed it to 40px and clipped the collapsed icon. */}
      <div
        data-testid="sidebar-brand"
        className="flex shrink-0 items-center justify-between h-16 px-4 border-b border-sidebar-border"
      >
        <div className="flex flex-1 items-center justify-center gap-2">
          {collapsed ? (
            <img
              src={iconUrl}
              alt={brandAlt}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <img
              src={logoUrl}
              alt={brandAlt}
              className="h-10 max-w-full object-contain"
            />
          )}
        </div>

        {/* Mobile close button */}
        <Tip text="Close navigation">
          <button
            onClick={onMobileClose}
            aria-label="Close navigation"
            className="lg:hidden p-1 rounded-md hover:bg-sidebar-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </Tip>
      </div>

      {/* The one scroll region between the pinned brand row and footer
          (SA-958). Its density is navBudget.ts's (SA-964): one rule, above a
          labeled group (Build); an unlabeled group after the first (Admin)
          is set apart by space alone. overflow-x-hidden keeps a label that
          does not truncate from showing a sideways scrollbar while the rail
          widens. */}
      <div
        data-testid="sidebar-scroll"
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2"
      >
        {nav.sidebar.map((group, i) => (
          <nav
            key={group.id}
            className={cn(i > 0 && (group.label ? 'mt-2 border-t border-sidebar-border pt-2' : 'mt-3'))}
          >
            {/* Plain text, never a toggle (spec §1.8); on the collapsed rail
                the rule above it is all that shows (spec §5). */}
            {!collapsed && group.label && (
              <div className="flex h-7 items-end px-3 pb-1 text-xs font-medium tracking-wide text-muted-foreground">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5">
              {group.rows.map((row) => (
                <RowLink key={row.id} row={row} collapsed={collapsed} onClick={onMobileClose} />
              ))}
            </div>
          </nav>
        ))}
      </div>

      {/* The footer, pinned below the scroll region (SA-962, spec §3.2) with
          no rule above it (SA-964). Expanded, Collapse is an icon at the user
          row's right end; collapsed, it sits below the centered avatar.
          Desktop only: the phone drawer has no Collapse. */}
      <div
        data-testid="sidebar-footer"
        className={cn('shrink-0 p-2', collapsed ? 'flex flex-col gap-0.5' : 'flex items-center gap-1')}
      >
        <div className={cn(!collapsed && 'min-w-0 flex-1')}>
          <UserMenu collapsed={collapsed} onNavigate={onMobileClose} />
        </div>
        {/* SA-1306: Help & feedback, an icon at the user row's end beside
            Collapse, so it costs the rail no height (navBudget). The collapsed
            rail has no room for it; the user menu and `?` still open it. */}
        {!collapsed && helpFeedback.available && (
          <Tip text={ENTRY_TIP} side="top">
            <button
              type="button"
              data-testid="sidebar-help-feedback"
              onClick={() => {
                helpFeedback.open()
                onMobileClose()
              }}
              aria-label={ENTRY_LABEL}
              aria-keyshortcuts={HELP_SHORTCUT}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                'text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring',
              )}
            >
              <MessageCircleQuestion className="size-4.5" />
            </button>
          </Tip>
        )}
        <Tip text={collapsed ? 'Expand navigation' : 'Collapse navigation'} side="right">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'hidden lg:flex h-8 shrink-0 items-center justify-center rounded-md',
              'text-sidebar-foreground/70 hover:bg-sidebar-accent transition-colors',
              'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring',
              collapsed ? 'w-full' : 'w-8'
            )}
          >
            {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
          </button>
        </Tip>
      </div>

      {/* SA-1034 PR 5 (spec §3.5): a pointer affordance on the rail's own
          edge — a 12px hover strip revealing a chevron tab at mid-height. It
          is pointer-only (aria-hidden, out of the tab order): the footer
          button stays the keyboard and screen-reader route, so role queries
          still find one Collapse. Absolutely positioned wholly OUTSIDE the
          aside's edge (inside it would cover the nav's scrollbar, review fix),
          so no page width moves. Desktop only. */}
      <Tip text={collapsed ? 'Expand navigation' : 'Collapse navigation'} side="right">
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="sidebar-rail-toggle"
          onClick={onToggleCollapse}
          className="group/rail absolute inset-y-0 left-full z-10 hidden w-3 cursor-pointer lg:block print:hidden"
        >
          <span className={cn(
            'absolute top-1/2 left-1/2 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full',
            'border border-sidebar-border bg-background text-sidebar-foreground/70 shadow-xs',
            'opacity-0 transition-opacity group-hover/rail:opacity-100 group-focus-visible/rail:opacity-100',
          )}>
            {collapsed ? <ChevronRight className="size-3" /> : <ChevronLeft className="size-3" />}
          </span>
        </button>
      </Tip>
    </aside>
  )
}
